import { useState, useEffect } from 'react';
import { api, ApiError } from '../lib/api';
import { CreateEmailAllowList, EmailAllowListResponse } from '@leads-platform/shared';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { useToast } from '../components/ui/toast';
import { fmtDate } from '../lib/format';

/**
 * The EmailOversight allow-list kinds (shared `allowListKindEnum`).
 */
const KINDS = { email: 'Email address', domain: 'Domain' } as const;
type AllowKind = keyof typeof KINDS;

function toAllowKind(value: string): AllowKind {
  return value === 'domain' ? 'domain' : 'email';
}

interface FormData {
  kind: AllowKind;
  value: string;
  note: string;
}

const initialFormState: FormData = {
  kind: 'domain',
  value: '',
  note: '',
};

/**
 * EmailOversight allow-list (Q93, epic 024 task 07) — emails and domains that
 * bypass EmailOversight on every capture style. Add + delete only: entries are
 * single facts, and deleting one simply restores vendor verification on the
 * next capture. Turnstile is never bypassed.
 */
export function AllowList() {
  const [entries, setEntries] = useState<EmailAllowListResponse[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>(initialFormState);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { success, error: toastError } = useToast();

  const loadEntries = () => {
    api
      .getAllowList()
      .then(setEntries)
      .catch((err: ApiError) => setError(err.message));
  };

  useEffect(() => {
    loadEntries();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);

    try {
      const data: CreateEmailAllowList = {
        kind: formData.kind,
        value: formData.value.trim().toLowerCase(),
        ...(formData.note.trim() ? { note: formData.note.trim() } : {}),
      };
      await api.createAllowList(data);
      success('Allow-list entry created successfully');

      setIsDialogOpen(false);
      setFormData(initialFormState);
      loadEntries();
    } catch (err) {
      const apiError = err as ApiError;
      const fieldErrors = apiError.validationErrors
        ? Object.values(apiError.validationErrors).flat().join(' ')
        : '';
      setFormError([apiError.message, fieldErrors].filter(Boolean).join(': '));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (entry: EmailAllowListResponse) => {
    if (
      !confirm(
        `Remove "${entry.value}" from the allow list? EmailOversight verification resumes for it on the next capture.`
      )
    )
      return;

    try {
      await api.deleteAllowList(entry.id);
      success(`Allow-list entry "${entry.value}" deleted successfully`);
      loadEntries();
    } catch (err) {
      const apiError = err as ApiError;
      toastError(apiError.message || 'Failed to delete allow-list entry');
    }
  };

  if (error) {
    return (
      <div>
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          Error loading allow list: {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Email Allow List</h1>
          <p className="text-muted-foreground mt-2">
            Emails and domains that skip EmailOversight validation on every capture style. Bypasses
            EmailOversight only — Turnstile still applies.
          </p>
        </div>
        <Button onClick={() => setIsDialogOpen(true)}>Add Entry</Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Entries</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Kind</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                    No allow-list entries. EmailOversight verifies every address until you add one.
                  </TableCell>
                </TableRow>
              ) : (
                entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="text-muted-foreground">{KINDS[entry.kind]}</TableCell>
                    <TableCell className="font-mono font-medium">{entry.value}</TableCell>
                    <TableCell className="text-muted-foreground">{entry.note ?? '—'}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {fmtDate(entry.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(entry)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Allow-List Entry</DialogTitle>
            <DialogDescription>
              Bypasses EmailOversight only — Turnstile still applies.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              {formError && <p className="text-sm text-destructive">{formError}</p>}
              <div className="space-y-2">
                <Label htmlFor="kind">Kind</Label>
                <Select
                  id="kind"
                  value={formData.kind}
                  onChange={(e) => setFormData({ ...formData, kind: toAllowKind(e.target.value) })}
                >
                  {Object.entries(KINDS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="value">Value</Label>
                <Input
                  id="value"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  required
                  placeholder={
                    formData.kind === 'domain' ? 'e.g., trading-tips.us' : 'e.g., name@example.com'
                  }
                />
                <p className="text-xs text-muted-foreground">
                  {formData.kind === 'domain'
                    ? 'Every address at this domain bypasses EmailOversight.'
                    : 'Exactly this address bypasses EmailOversight.'}{' '}
                  Stored lowercase.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="note">Note (optional)</Label>
                <Input
                  id="note"
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  placeholder="e.g., Chelsie catch-all"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? 'Saving...' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
